import useSWR from "swr";
import { useRef, useEffect, useCallback } from "react";
import type { UseChatHelpers } from "@ai-sdk/react";

type ScrollFlag = ScrollBehavior | false;

export function useScrollToBottom(status?: UseChatHelpers["status"]) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const endRef = useRef<HTMLDivElement | null>(null);

  const { data: isAtBottom = false, mutate: setIsAtBottom } = useSWR(
    "messages:is-at-bottom",
    null,
    { fallbackData: false }
  );

  const { data: scrollBehavior = false, mutate: setScrollBehavior } =
    useSWR<ScrollFlag>("messages:should-scroll", null, { fallbackData: false });

  useEffect(() => {
    if (scrollBehavior && containerRef.current) {
      containerRef.current.scrollTo({
        top: containerRef.current.scrollHeight,
        behavior: scrollBehavior,
      });
      setScrollBehavior(false);
    }
  }, [setScrollBehavior, scrollBehavior]);

  // Long chats are virtualized, so do NOT observe the whole message subtree.
  // A subtree MutationObserver scales with every token, chart, iframe, and
  // markdown node, which is exactly what made long mobile chats slow/crashy.
  // The list virtualizer now owns follow-to-bottom during streaming.

  const scrollToBottom = useCallback(
    (behavior: ScrollBehavior = "smooth") => {
      setScrollBehavior(behavior);
    },
    [setScrollBehavior]
  );

  function onViewportEnter() {
    setIsAtBottom(true);
  }

  function onViewportLeave() {
    setIsAtBottom(false);
  }

  return {
    containerRef,
    endRef,
    isAtBottom,
    scrollToBottom,
    onViewportEnter,
    onViewportLeave,
  };
}
